import { useEffect, useState } from 'react';
import { ArrowUpRight, Clock3, RefreshCw, Search, ShieldCheck, XCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';

const FILTERS = [
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'resubmit', label: 'Resubmit' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'incomplete', label: 'Incomplete' },
  { key: '', label: 'All' },
];

export default function AdminProviders() {
  const [filter, setFilter] = useState('pending');
  const [query, setQuery] = useState('');
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(filter ? `/admin/providers?status=${filter}` : '/admin/providers');
      setProviders(data.providers || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load providers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filter]);

  const filtered = providers.filter((p) => {
    const haystack = [p.name, p.phone, p.email, p.city, p.category, p.reference_number].filter(Boolean).join(' ').toLowerCase();
    return haystack.includes(query.trim().toLowerCase());
  });

  return (
    <div className="admin-page">
      <div className="admin-page-head">
        <div>
          <div className="admin-eyebrow"><ShieldCheck size={13} /> Provider operations</div>
          <h1>Providers & KYC</h1>
          <p>Process the verification queue with one searchable workspace. Open a profile only when an action is needed.</p>
        </div>
        <button className="admin-lux-button secondary" onClick={load} disabled={loading}><RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Refresh</button>
      </div>

      <div className="admin-card">
        <div className="admin-card-head">
          <div><h2>Verification queue</h2><p>{filtered.length} providers shown</p></div>
        </div>
        <div className="admin-filter-row">
          {FILTERS.map((f) => <button key={f.key || 'all'} className={filter === f.key ? 'active' : ''} onClick={() => setFilter(f.key)}>{f.label}</button>)}
        </div>
        <div style={{ padding: '0 22px 16px' }}>
          <div style={{ position: 'relative', maxWidth: 460 }}>
            <Search size={15} style={{ position: 'absolute', left: 11, top: 13, color: '#9aa4ae' }} />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, phone, city, category or reference number" style={{ paddingLeft: 36 }} />
          </div>
        </div>

        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead><tr><th>Provider</th><th>Category</th><th>Location</th><th>Status</th><th>Verification</th><th /></tr></thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id}>
                  <td><div className="admin-person-cell"><div className="admin-table-avatar">{p.name?.slice(0,1)?.toUpperCase() || 'P'}</div><div><strong>{p.name || 'Unnamed'}</strong><span>{p.phone || p.email || 'No contact'}</span></div></div></td>
                  <td>{p.category || 'Not selected'}</td>
                  <td>{p.city || '—'}</td>
                  <td><Status status={p.approval_status} /></td>
                  <td>{p.verification?.identity_complete ? <span className="admin-status success">Identity complete</span> : <span className="admin-status warning">Review needed</span>}</td>
                  <td><Link className="admin-row-actions" to={`/admin/providers/${p.id}`}><button>Open <ArrowUpRight size={14} /></button></Link></td>
                </tr>
              ))}
              {!loading && !filtered.length && <tr><td colSpan="6"><div className="admin-empty"><Clock3 size={18} /> No providers match this queue.</div></td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Status({ status }) {
  const map = {
    approved: ['Approved', 'success', ShieldCheck],
    pending: ['Pending', 'warning', Clock3],
    resubmit: ['Needs resubmission', 'danger', Clock3],
    rejected: ['Rejected', 'danger', XCircle],
    incomplete: ['Incomplete', 'neutral', Clock3],
  };
  const [label, tone, Icon] = map[status] || ['Unknown', 'neutral', Clock3];
  return <span className={`admin-status ${tone}`}><Icon size={11} style={{ marginRight: 4 }} />{label}</span>;
}
